/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * useBlockOperations - Block Tab Management and Code Generation
 *
 * Manages block-related operations including tab creation, code generation
 * from metadata, step dispatching across tabs, and block code formatting.
 */

import { useTabManagerContext, DEBUG_TAB_MODIFICATION } from '../context/TabManagerContext';
import { generateBlockCode } from '../helpers/blockCodeGenerator';
import { createNewTab, getTabKey, setIdRecursive } from '../helpers/codeEditorHelpers';
import { DecorationManager } from '../managers/DecorationManager';
import { MetadataManager } from '../managers/MetadataManager';
import { StepConverter } from '../managers/StepConverter';
import { FunctionMetadata } from '../types/LiveAgentEditorTypes';

import { CachedTab } from './types';
import { useStepEnricher } from './useStepEnricher';

import { toast } from '@/components/ui/use-toast';
import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

/**
 * Block information extracted from metadata
 */
interface BlockInfo {
    blockSteps: UIStep[];
    blockDefArgs: string[];
    blockExists: boolean;
}

export const useBlockOperations = () => {
    const context = useTabManagerContext();
    const {
        editorRef,
        activeTabIdRef,
        tabsRef,
        tabContentRef,
        tabOriginalContentRef,
        blockCacheRef,
        setTabs,
        setActiveTabId,
        blocksData,
        astParser,
    } = context;

    // Use the new step enricher hook
    const { enrichBlockSteps, updateNestedBlockSteps } = useStepEnricher();

    /** Extract block information from metadata and block definitions */
    const extractBlockInfo = (
        blockName: string,
        metadata: FunctionMetadata,
        generateDefaultArgs: boolean = false,
    ): BlockInfo => {
        // Get block steps from metadata first, then fallback to blocksData
        let blockSteps = metadata.steps || [];
        let blockDefArgs: string[] = [];

        // Check if the block exists in blocksData
        const matchingBlock = blocksData?.find((block) => !block.archived && block.name === blockName);

        // If no block steps in metadata, try to find them in blocksData
        if (blockSteps.length === 0 && matchingBlock && matchingBlock.steps) {
            blockSteps = structuredClone(matchingBlock.steps);
            setIdRecursive(blockSteps);
        }

        // Use blockDefArgs from blocksData if the block exists
        if (matchingBlock && matchingBlock.blockDefArgs) {
            blockDefArgs = structuredClone(matchingBlock.blockDefArgs);
        }
        // Only generate default arguments if explicitly requested AND block doesn't exist in blocksData
        else if (generateDefaultArgs && !matchingBlock) {
            const args = metadata.call.args || [];
            blockDefArgs = args.map((_: any, index: number) => `arg${index + 1}`);
        }

        return { blockSteps, blockDefArgs, blockExists: !!matchingBlock };
    };

    /** Generate block code from metadata */
    const generateBlockCodeFromMetadata = async (
        metadata: FunctionMetadata,
    ): Promise<{ blockName: string; blockCode: string }> => {
        // Extract block name from method name (e.g., "blocks.login" -> "login")
        const blockName = metadata.call.methodName.split('.')[1];

        const { blockSteps, blockDefArgs } = extractBlockInfo(blockName, metadata, true);

        const blockCode = await generateBlockCode(blockName, blockSteps, blockDefArgs);

        return { blockName, blockCode };
    };

    /**
     * Open a block tab with given content
     * @param blockName - Name of the block
     * @param blockCode - Code content for the block
     * @param tabSteps - Steps for the block
     * @param cacheCurrentTab - Function to cache current tab before switching
     * @param clearAllDecorations - Function to clear editor decorations
     */
    const openBlockTab = (
        blockName: string,
        blockCode: string,
        tabSteps: UIStep[],
        cacheCurrentTab: () => void,
        clearAllDecorations: () => void,
        switchTab?: (tabId: string) => void,
    ) => {
        if (!editorRef.current) return;

        const tabId = `block-${blockName}`;

        // Check if tab already exists
        const existingTab = tabsRef.current.find((tab) => tab.id === tabId);
        if (existingTab) {
            // Tab already exists, just switch to it
            if (switchTab) {
                switchTab(tabId);
            } else {
                setActiveTabId(tabId);
                activeTabIdRef.current = tabId;
            }
            return;
        }

        // Cache current tab before opening new one
        cacheCurrentTab();

        // Clear decorations
        clearAllDecorations();

        // Check if we have cached data for this block
        const cachedTab = blockCacheRef.current.get(blockName);

        // Use cached content if available, otherwise use generated code
        const finalBlockCode = cachedTab?.code || blockCode;

        // Store tab content
        tabContentRef.current.set(tabId, finalBlockCode);

        // Store original content for modification tracking
        if (cachedTab) {
            tabOriginalContentRef.current.set(tabId, cachedTab.originalCode);
        } else {
            tabOriginalContentRef.current.set(tabId, blockCode);
        }

        // Create new tab with managers
        const newTab = createNewTab(
            tabId,
            blockName,
            finalBlockCode,
            false,
            cachedTab?.isModified || tabSteps.length === 0,
            cachedTab?.metadataManager || new MetadataManager(),
            cachedTab?.decorationManager,
        );

        // If no cached decoration manager, create a new one
        if (!newTab.decorationManager && newTab.metadataManager) {
            newTab.decorationManager = new DecorationManager(editorRef.current, newTab.metadataManager);
        }

        // Add new tab
        setTabs((prevTabs) => {
            const newTabs = [...prevTabs, newTab];
            tabsRef.current = newTabs;
            return newTabs;
        });

        // Update editor content
        editorRef.current.setValue(finalBlockCode);

        setActiveTabId(tabId);
        activeTabIdRef.current = tabId;

        // Trigger re-render for tab bar
        if (context.triggerRerender) {
            context.triggerRerender();
        }
    };

    /**
     * Open block tab from metadata
     * @param metadata - Block metadata
     * @param cacheCurrentTab - Function to cache current tab
     * @param clearAllDecorations - Function to clear decorations
     * @param getCachedTab - Function to get cached tab data
     */
    const openBlockTabFromMetadata = async (
        metadata: FunctionMetadata,
        cacheCurrentTab: () => void,
        clearAllDecorations: () => void,
        getCachedTab: (key: string) => CachedTab | undefined,
        switchTab?: (tabId: string) => void,
    ) => {
        // Extract block name from method name (e.g., "blocks.login" -> "login")
        const blockName = metadata.call.methodName.split('.')[1];

        // Check cache first
        const cachedTab = getCachedTab(blockName);
        if (cachedTab) {
            // Use cached version
            openBlockTab(blockName, cachedTab.code, cachedTab.steps, cacheCurrentTab, clearAllDecorations, switchTab);
        } else {
            // Generate new block code
            const { blockName: generatedBlockName, blockCode } = await generateBlockCodeFromMetadata(metadata);

            // Get block information using integrated function
            const { blockSteps } = extractBlockInfo(blockName, metadata);

            openBlockTab(generatedBlockName, blockCode, blockSteps, cacheCurrentTab, clearAllDecorations, switchTab);
        }
    };

    /**
     * Dispatch block steps to update all references
     * @param blockName - Name of block to update
     * @param blockSteps - New steps for the block
     * @param visitedBlocks - Set of visited blocks (for circular dependency detection)
     * @returns True if successful, false if circular dependency detected
     */
    const dispatchBlockSteps = (
        blockName: string,
        blockSteps: UIStep[],
        visitedBlocks: Set<string> = new Set(),
    ): boolean => {
        // Check for circular dependency
        if (visitedBlocks.has(blockName)) {
            toast({
                title: 'Error: Circular dependency detected',
                description: `Block "${blockName}" depends on itself`,
                variant: 'destructive',
            });
            return false; // Circular dependency detected
        }

        // Add current block to visited set
        const newVisitedBlocks = new Set(visitedBlocks);
        newVisitedBlocks.add(blockName);

        // Track which tabs were modified
        const modifiedTabs = new Set<string>();
        // Track which blocks were modified (for recursive dispatch)
        const modifiedBlocks = new Set<string>();

        // Update block cache for this block
        const blockCache = blockCacheRef.current.get(blockName);
        if (blockCache) {
            const metadataBefore = JSON.stringify(blockCache.metadataManager?.getMetadata());
            for (const step of blockSteps) {
                blockCache.metadataManager?.updateStatus(step, false);
            }
            const metadataAfter = JSON.stringify(blockCache.metadataManager?.getMetadata());
            if (metadataBefore !== metadataAfter) {
                const metadataBeforeObj = JSON.parse(metadataBefore);
                const metadataAfterObj = JSON.parse(metadataAfter);

                // Find differences between before and after metadata
                const diff: Record<string, { before: any; after: any }> = {};
                const allKeys = new Set([...Object.keys(metadataBeforeObj), ...Object.keys(metadataAfterObj)]);

                for (const key of allKeys) {
                    const beforeValue = JSON.stringify(metadataBeforeObj[key]);
                    const afterValue = JSON.stringify(metadataAfterObj[key]);
                    if (beforeValue !== afterValue) {
                        diff[key] = {
                            before: metadataBeforeObj[key],
                            after: metadataAfterObj[key],
                        };
                    }
                }

                if (DEBUG_TAB_MODIFICATION) {
                    // eslint-disable-next-line no-console
                    console.log(`[TAB_MODIFICATION] Setting blockCache.isModified = true (dispatchBlockSteps)`, {
                        blockName,
                        reason: 'metadata changed after updating block steps',
                        location: 'useBlockOperations.dispatchBlockSteps',
                        diff,
                    });
                }
                blockCache.isModified = true;
                modifiedTabs.add(blockName);
            }
        }

        // Update the metadata of tabs containing this block
        for (const tab of tabsRef.current) {
            if (tab.metadataManager) {
                // Update metadata manager
                const hasUpdates = tab.metadataManager.updateBlockSteps(blockName, blockSteps);

                // If this tab had updates, mark it as modified
                if (hasUpdates) {
                    const tabKey = getTabKey(tab.id);
                    modifiedTabs.add(tabKey);

                    // If this is a block tab, add it to blocks that need recursive dispatch
                    if (tab.id.startsWith('block-')) {
                        const tabBlockName = getTabKey(tab.id);
                        modifiedBlocks.add(tabBlockName);
                    }
                }
            }
        }

        // Update cached tabs that contain this block using the hook function
        for (const [cacheKey, cachedTab] of blockCacheRef.current.entries()) {
            if (cacheKey === blockName) {
                continue;
            }

            if (cachedTab.steps) {
                const updatedSteps = updateNestedBlockSteps(cachedTab.steps, blockName, blockSteps);

                // Check if any steps were actually updated
                if (JSON.stringify(updatedSteps) !== JSON.stringify(cachedTab.steps)) {
                    if (DEBUG_TAB_MODIFICATION) {
                        // eslint-disable-next-line no-console
                        console.log(`[TAB_MODIFICATION] Setting cached tab isModified = true (dispatchBlockSteps)`, {
                            cacheKey,
                            reason: 'nested block steps updated',
                            location: 'useBlockOperations.dispatchBlockSteps',
                            stepsChanged: true,
                        });
                    }
                    blockCacheRef.current.set(cacheKey, {
                        ...cachedTab,
                        steps: updatedSteps,
                        isModified: true,
                    });
                    modifiedTabs.add(cacheKey);
                    modifiedBlocks.add(cacheKey);
                }
                // Update the metadata manager of the tab
                const tab = tabsRef.current.find((t) => t.name === cacheKey);
                tab?.metadataManager?.updateBlockSteps(blockName, blockSteps);
            }
        }

        // Update visual state for modified tabs
        if (modifiedTabs.size > 0) {
            setTabs((prevTabs) => {
                const updatedTabs = prevTabs.map((tab) => {
                    const tabKey = getTabKey(tab.id);
                    if (modifiedTabs.has(tabKey)) {
                        if (DEBUG_TAB_MODIFICATION) {
                            // eslint-disable-next-line no-console
                            console.log(
                                `[TAB_MODIFICATION] Setting tab.isModified = true (dispatchBlockSteps - visual update)`,
                                {
                                    tabId: tab.id,
                                    tabKey,
                                    reason: 'block steps dispatched affecting this tab',
                                    location: 'useBlockOperations.dispatchBlockSteps.setTabs',
                                },
                            );
                        }
                        return { ...tab, isModified: true };
                    }
                    return tab;
                });
                tabsRef.current = updatedTabs;
                return updatedTabs;
            });
        }

        // Recursively dispatch updates for all modified blocks (deduplicated)
        for (const modifiedBlockName of modifiedBlocks) {
            // Get the updated steps for this modified block
            let updatedBlockSteps: UIStep[] = [];

            // Try to get steps from cache first
            const cachedBlock = blockCacheRef.current.get(modifiedBlockName);
            if (cachedBlock?.steps) {
                updatedBlockSteps = cachedBlock.steps;
            } else {
                // Fallback to getting steps from the active tab if it's the current block
                const blockTabId = `block-${modifiedBlockName}`;
                if (activeTabIdRef.current === blockTabId) {
                    updatedBlockSteps = getBlockSteps();
                } else {
                    // Get steps from blocksData as last resort
                    const matchingBlock = blocksData?.find(
                        (block) => !block.archived && block.name === modifiedBlockName,
                    );
                    if (matchingBlock?.steps) {
                        updatedBlockSteps = structuredClone(matchingBlock.steps);
                        setIdRecursive(updatedBlockSteps);
                    }
                }
            }

            // Recursively dispatch the updated steps
            if (updatedBlockSteps.length > 0) {
                const success = dispatchBlockSteps(modifiedBlockName, updatedBlockSteps, newVisitedBlocks);
                if (!success) {
                    return false; // Circular dependency detected in recursive call
                }
            }
        }

        return true; // Success, no circular dependencies
    };

    /**
     * Get current block steps from active tab
     * @returns Current steps from active tab
     */
    const getBlockSteps = (): UIStep[] => {
        try {
            const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
            if (!activeTab?.metadataManager || !editorRef.current) return [];

            const code = tabContentRef.current.get(activeTabIdRef.current) || '';
            return StepConverter.codeToSteps(code, editorRef.current, astParser, activeTab.metadataManager);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error getting block steps by id', e);
            return [];
        }
    };

    return {
        openBlockTabFromMetadata,
        dispatchBlockSteps,
        generateBlockCodeFromMetadata,
        getBlockSteps,
        enrichBlockSteps, // Now from useStepEnricher hook
        updateNestedBlockSteps, // Now from useStepEnricher hook
    };
};
